import { useIsCurrentPageType, useMatchLargeViewport, useStaticData } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { styled } from '@glitz/react';
import { CountrySelector } from 'webapp/shell/header/CountrySelector';
import * as React from 'react';
import { Block, edgeMarginsDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { ZIndex } from 'webapp/z-index';
import VerticalCarousel from './VerticalCarousel';
import { isNordicNest, isSvenssons } from 'webapp/theme';
import Link from 'webapp/components/Link';
import { CheckIcon } from 'webapp/components/icons';
import { useIsCheckoutFlow } from 'webapp/shell/header/use-is-checkout-flow';
import { BANNER_DESKTOP_HEIGHT, BANNER_MOBILE_HEIGHT, CENTER_TOP_MENU } from 'webapp/utils/constants';

export default React.memo(function SiteBanner() {
  const siteBanner = useStaticData<NordicNest.Api.SiteBannerApiModel>();
  const isLargeViewport = useMatchLargeViewport();
  const menu = useStaticData<NordicNest.Api.CfMenuApiModel>();
  const checkoutflow = useIsCheckoutFlow();
  const isExpressCheckoutPage = useIsCurrentPageType<NordicNest.Api.ExpressCheckoutPageApiModel>();

  return (isSvenssons && checkoutflow) || isExpressCheckoutPage ? null : (
    <>
      <Banner
        css={{
          backgroundColor: t => siteBanner?.backgroundColor ?? t.topBanner?.color,
          color: t => siteBanner?.textColor ?? t.topBanner?.text,
        }}
      >
        <Content>
          {CENTER_TOP_MENU && isLargeViewport && (
            <LeftContainer>
              {menu?.primaryTopLinks.map((link, i) => <PageItems key={i} {...link.link} />)}
            </LeftContainer>
          )}
          <Messages>
            {siteBanner?.messages.map((message, index) => (
              <Message key={index} css={{ color: message?.textColor ?? 'inherit' }}>
                <Check />
                <Document styleDecorators={styles} {...message.content} />
              </Message>
            ))}
          </Messages>
          {isLargeViewport &&
            (isSvenssons ? (
              <Container css={rightDecorator}>
                {menu?.secondaryTopLinks.map((link, i) => <PageItems key={i} {...link.link} />)}
              </Container>
            ) : (
              <CountrySelector css={rightDecorator} />
            ))}
        </Content>
      </Banner>
    </>
  );
});

const Banner = styled.div();

const Content = styled(
  Block,
  edgeMarginsDecorator,
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({ height: BANNER_DESKTOP_HEIGHT }),
  {
    display: 'grid',
    gridTemplateAreas: '"left center right"',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    position: 'relative',
    height: BANNER_MOBILE_HEIGHT,
    zIndex: ZIndex.SiteBanner,
  },
);

const styles: StyleDecorators = {
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
    fontSize: t => t.sharedFontSize.xxs,
    whiteSpace: 'nowrap',
  }),
};

const Messages = styled(VerticalCarousel, {
  position: 'relative',
  gridArea: 'center',
  display: 'flex',
  height: 'inherit',
  justifyContent: 'center',
  width: '100%',
  maxWidth: 500,
});

const Message = styled.div({
  opacity: 0,
  position: 'absolute',
  bottom: 0,
  transform: 'translateY(50%)',
  willChange: 'transform, opacity',
  pointerEvents: 'none',
  height: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: t => t.sharedFontSize.xxs,
  gap: t => t.sharedSpacing.xs,
});

const Check = styled(CheckIcon, {
  color: t => t.topBanner?.checkColor,
  width: isNordicNest ? 24 : 14,
  height: isNordicNest ? 24 : 14,
});

const Container = styled.div({
  display: 'flex',
});

const LeftContainer = styled(Container, {
  gridArea: 'left',
});

/** @glitz-dynamic */
const TopMenuLink = styled(Link, {
  textDecoration: 'none',
});

const PageItems = styled(TopMenuLink, {
  marginLeft: t => t.sharedSpacing.m,
  fontSize: t => t.sharedFontSize.xxs,
  ':first-child': {
    marginLeft: 0,
  },
  whiteSpace: 'nowrap',
});

const rightDecorator = styled({
  gridArea: 'right',
  justifySelf: 'end',
});
